import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { GONE_REASONS } from "../shared/editorSupport";
import type DataMapperTargetStore from "../Target/DataMapperTargetStore";
import { DATA_MAPPER_TARGET_ROUTE } from "../Target/DataMapperTargetStore";
import { subscribeTabEvents } from "../tabEvents";
import { toWhere, type BrowseFilter, type WhereCondition } from "../Browse/DataMapperBrowseStore";
import {
  DATA_MAPPER_IMPORTS_PATH,
  DATA_MAPPER_IMPORT_EVENTS,
  NOT_CONNECTED_REASONS,
  SQL_HOST_NOT_ALLOWED_MESSAGE,
  refusalReason,
  sameId,
  visibleToSelection,
  type DataMapperImportEventPayload,
  type DataMapperImportMode,
  type DataMapperImportPhase,
  type DataMapperImportReport,
  type DataMapperImportStatus,
  type DataMapperImportSummary,
  type DataMapperImportView,
  type DataMapperMappingConfig,
  type DataMapperProfile,
  type DataMapperWriteMode,
} from "../dataMapper.types";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
export const DATA_MAPPER_SOURCE_ROUTE = "Data Mapper Source";

/** 409 'target-busy', as a sentence. */
export const TARGET_BUSY_MESSAGE =
  "Another write import is already running against this target database — from another tab of yours " +
  "or from someone else. HQ writes one import at a time into a database: wait for it to finish, then start yours again.";

/** 409 'import-committing' on Cancel, as a sentence. */
export const TOO_LATE_TO_CANCEL_MESSAGE =
  "Too late to cancel: the import is already committing. Its result appears here when the commit finishes.";

export const NO_SOURCE_MESSAGE =
  "This tab has no source open for this profile. Connect one on the Data Source screen first.";
export const NO_TARGET_MESSAGE =
  "This tab has no target database open for this profile. Connect one on the Target screen first.";

/**
 * The events carry a running import's progress; reading it is only the
 * fallback. Once no event has arrived for IMPORT_EVENT_SILENCE_MS (the API
 * emits progress at least every half second while rows move, and on every
 * phase change), the import is read, and read again every IMPORT_POLL_MS until
 * an event arrives or it finishes: a socket reconnect, or a dry run that
 * finished before its id was known here, cannot leave the screen stuck.
 */
export const IMPORT_EVENT_SILENCE_MS = 10_000;

/** Filters one source table may carry (data-mapper-imports' schema: maxItems 50). */
export const MAX_SOURCE_FILTERS = 50;
export const IMPORT_POLL_MS = 3000;

/** The import events this screen listens to, on `data-mapper-imports`. */
const IMPORT_EVENT_NAMES = [
  DATA_MAPPER_IMPORT_EVENTS.progress,
  DATA_MAPPER_IMPORT_EVENTS.complete,
  DATA_MAPPER_IMPORT_EVENTS.error,
] as const;

/**
 * 'regex-too-slow': a regex (a transform step, or the target schema's pattern
 * on a field) took longer than its budget on a batch of rows. HQ runs an
 * import's regexes off its main thread and stops them there. The API's message
 * names the mapping and the step (or the field) and never a row value; this
 * sentence adds what to do about it.
 */
export const REGEX_TOO_SLOW_ADVICE =
  "A pattern with nested repeats, such as (a+)+, can take exponentially long on some values. " +
  "Simplify that regex, then run the import again.";

export function regexTooSlowSentence(apiMessage: unknown): string {
  const detail =
    typeof apiMessage === "string" && apiMessage
      ? apiMessage
      : "A regex took too long on this data, so the import stopped.";
  return `${detail} ${REGEX_TOO_SLOW_ADVICE}`;
}

/**
 * A finished import's `error.reason` (data-plane/imports.ts; the driver's
 * codes come from mssql-adapter.ts dbFailure) as the sentence the screen
 * shows. Matched on the reason, never on the message text. Null adds nothing
 * to the outcome line (a plain cancel already reads "Cancelled.").
 */
export function importFailureSentence(error: { reason?: unknown; message?: unknown } | null): string | null {
  if (!error) return null;
  switch (error.reason) {
    case "import-cancelled":
      return null;
    case "import-aborted":
      return (
        "The import stopped because this tab's source or target connection closed: it was disconnected or " +
        "replaced, it expired while idle, or HQ's API restarted. Connect again, then run the import again."
      );
    case "rollback-failed":
      return (
        "The import failed and HQ could not confirm the rollback, so it closed the target connection " +
        "(SQL Server rolls back an open transaction when its connection closes). Check the target tables, then connect again."
      );
    case "db-error":
      return "A SQL Server error stopped the import. HQ's log has the SQL Server error number.";
    case "login-failed":
      return "The SQL Server refused the login while the import was running.";
    case "unreachable":
      return "HQ lost its connection to the SQL Server while the import was running.";
    case "empty-mapping":
      return "The mapping configuration maps no source columns. Map at least one on the Map screen and save it.";
    case "target-not-connected":
      return NO_TARGET_MESSAGE;
    case "regex-too-slow":
      return regexTooSlowSentence(error.message);
    default:
      return typeof error.message === "string" && error.message ? error.message : "The import failed.";
  }
}

/** summary.errorsTruncated / report.errorsTruncated as a sentence. */
export const errorsTruncatedSentence = (errorCount: number): string =>
  `The import found ${errorCount.toLocaleString()} row errors, more than HQ keeps: only the first of them are listed.`;

/** An import refusal (start, cancel, or the import's own final error) as the sentence the screen shows. */
export function importErrorMessage(err: unknown, fallback: string): string {
  const reason = refusalReason(err) ?? (err as { reason?: unknown } | null)?.reason;
  if (reason === "target-busy") return TARGET_BUSY_MESSAGE;
  if (reason === "import-committing") return TOO_LATE_TO_CANCEL_MESSAGE;
  if (reason === "sql-host-not-allowed") return SQL_HOST_NOT_ALLOWED_MESSAGE;
  if (reason === "target-not-connected") return NO_TARGET_MESSAGE;
  if (typeof reason === "string" && NOT_CONNECTED_REASONS.has(reason)) return NO_SOURCE_MESSAGE;
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === "string" && message && !(err instanceof Error)) return message;
  return loadErrorMessage(err, fallback);
}

/** Where a Cancel stands. */
export type CancelState = "idle" | "requesting" | "too-late" | "failed";

const isFinished = (status: DataMapperImportStatus | null): boolean => status !== null && status !== "running";

/**
 * Data Mapper → Import: run the open profile's saved mapping configuration from
 * this tab's source into this tab's target, as ONE server-side job
 * (`data-mapper-imports`). A dry run reads, transforms and validates (per-table
 * counts, row errors, preview rows); a write does the same inside one target
 * transaction, after a confirmation that names the target and the write mode.
 * Ported from the standalone tool's Preview and Export panels (exportPipeline).
 *
 * WHAT IT HOLDS. The chosen config is an id; the configs stay in their
 * ServiceStore. The running import's status and its report are the data
 * plane's answer for one job — no HQ entity, no shared cache — held for the
 * page's life like the Browse screen's rows. The target's tables come from the
 * Target screen's store (one tab, one target).
 *
 * EVENTS. `import-progress` / `-complete` / `-error` arrive on the raw socket
 * client for every tab of this person; the shared `subscribeTabEvents`
 * (tabEvents.ts) drops other tabs', and this store drops any import that is
 * not the one it started. The events are the main path: a read of the import
 * is only the fallback after a silence (IMPORT_EVENT_SILENCE_MS).
 */
class DataMapperImportStore {
  rootStore: RootStore;

  mappingConfigId: string | null = null;
  /** Source table → "schema.table" on the target. An empty entry is left to the API (the config's schemaName). */
  targetTables: Record<string, string> = {};
  writeMode: DataMapperWriteMode = "insert";
  /** Source table → its filters (Browse's editor rows). Only complete, enabled rows are sent. */
  sourceFilters: Record<string, BrowseFilter[]> = {};
  /** Also write `_sourceTable` and `_sourceIndex` on every row (the target tables need both columns). */
  includeMetadata = false;
  /** Source table → its column names, for the filter editor (read from the open source on demand). */
  filterColumns: Record<string, string[]> = {};

  /** The write confirmation is open; only `confirmWrite` from here starts a write. */
  confirmOpen = false;
  /** The write mode the open confirmation names (it is what will be sent). */
  confirmWriteMode: DataMapperWriteMode = "insert";

  // This tab's source (the API's `status` answer).
  sourceConnected = false;
  sourceName: string | null = null;
  statusLoaded = false;

  configsLoading = false;
  configsError: string | null = null;
  configDetailError: string | null = null;

  // The current import
  starting: DataMapperImportMode | null = null;
  startError: string | null = null;
  importId: string | null = null;
  mode: DataMapperImportMode | null = null;
  importWriteMode: DataMapperWriteMode | null = null;
  status: DataMapperImportStatus | null = null;
  phase: DataMapperImportPhase | null = null;
  done = 0;
  total = 0;
  result: DataMapperImportReport | null = null;
  /** The final event's counts: shown until (and beside) the report the read brings. */
  summary: DataMapperImportSummary | null = null;
  importError: string | null = null;
  cancelState: CancelState = "idle";
  cancelMessage: string | null = null;

  private loadSeq = 0;
  private filterKey = 0;
  private unsubscribe: (() => void) | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** When this import was last heard of (started, or an event): the fallback read waits for a silence. */
  private lastHeardAt = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get importsService() {
    return dataPlaneService(this.rootStore, "data-mapper-imports");
  }

  private get configsService() {
    return dataMapperService(this.rootStore, "data-mapper-mapping-configs");
  }

  /** The Target screen's store: this tab's target and its tables. */
  get target(): DataMapperTargetStore | null {
    return (this.rootStore as unknown as { dataMapperTargetViewStore?: DataMapperTargetStore })
      .dataMapperTargetViewStore ?? null;
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  get tabId(): string | null {
    return this.selection?.tabId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  /** The saved mapping configurations this selection sees. */
  get configs(): DataMapperMappingConfig[] {
    if (!this.profileId) return [];
    const rows = (this.configsService?.entities ?? []) as unknown as DataMapperMappingConfig[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get selectedConfig(): DataMapperMappingConfig | null {
    return this.configs.find((c) => sameId(c.id, this.mappingConfigId)) ?? null;
  }

  /** The source tables the chosen config reads (the summary's list, else its mappings'). */
  get sourceTables(): string[] {
    const c = this.selectedConfig;
    if (!c) return [];
    if (c.sourceTables?.length) return [...c.sourceTables];
    return [...new Set((c.mappings ?? []).map((m) => m.sourceTable).filter(Boolean))];
  }

  /** The Map screen holds unsaved edits: the import runs the SAVED configuration. */
  get mappingDraftDirty(): boolean {
    return (this.rootStore as unknown as { dataMapperMappingViewStore?: { dirty?: boolean } })
      .dataMapperMappingViewStore?.dirty === true;
  }

  get targetConnected(): boolean {
    return this.target?.connected === true;
  }

  get running(): boolean {
    return this.status === "running";
  }

  get canStart(): boolean {
    return (
      this.canUse &&
      !!this.profileId &&
      !!this.selectedConfig &&
      this.sourceConnected &&
      this.starting === null &&
      !this.running
    );
  }

  get canWrite(): boolean {
    return this.canStart && this.targetConnected;
  }

  /** "Reading 400 of 1,000 rows" style progress, 0..100, or null while the total is unknown. */
  get percent(): number | null {
    if (!this.total) return null;
    return Math.min(100, Math.round((this.done / this.total) * 100));
  }

  /** The row-error list is cut (the report, else the final event's summary, says so). */
  get errorsTruncatedMessage(): string | null {
    const truncated = this.result?.errorsTruncated ?? this.summary?.errorsTruncated;
    if (!truncated) return null;
    return errorsTruncatedSentence(this.result?.errorCount ?? this.summary?.errorCount ?? 0);
  }

  /** The filters as the API's `sourceFilters`: this config's tables, complete and enabled rows only. */
  get sourceFiltersBody(): Record<string, WhereCondition[]> {
    const out: Record<string, WhereCondition[]> = {};
    for (const table of this.sourceTables) {
      const where = toWhere(this.sourceFilters[table] ?? []);
      if (where.length > 0) out[table] = where;
    }
    return out;
  }

  // ---- Route entry ----------------------------------------------------------

  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    this.subscribe();
    // An import started before leaving the page: catch up on what was missed.
    if (this.importId && this.running) {
      void this.readImport();
      this.scheduleFallback();
    }
    await this.load();
  }

  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.configsLoading = false;
      return;
    }
    if (!this.mappingConfigId && selection.selectedMappingConfigId) {
      this.mappingConfigId = selection.selectedMappingConfigId;
    }
    const query = { ...selection.selectionQuery };
    this.configsLoading = true;
    this.configsError = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // settle in between (one find failed on its own, the others were ended)
    // would still pass the seq check.
    const ended = sessionMark(this.rootStore);
    const results = await Promise.allSettled([
      this.configsService
        ? this.configsService.find({ query })
        : Promise.reject(new Error("The Data Mapper service is not available in this build.")),
      this.refreshSourceStatus(),
      this.target?.refresh(),
      dataMapperService(this.rootStore, "data-mapper-profiles")?.find({ query: { clientId: query.clientId } }),
    ]);
    const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    runInAction(() => {
      if (seq !== this.loadSeq || ended()) return;
      this.configsLoading = false;
      if (this.mappingConfigId && !this.selectedConfig) this.mappingConfigId = null;
      if (!failure || isSessionEnded(failure.reason)) return;
      const err: unknown = failure.reason;
      if (GONE_REASONS.has(refusalReason(err) ?? "") && query.profileId) {
        selection.forgetProfile(query.profileId);
        this.configsError = "The profile that was open no longer exists. Choose one on the Profiles page.";
      } else {
        this.configsError = loadErrorMessage(err, "The mapping configurations could not be loaded.");
      }
    });
  }

  private async refreshSourceStatus(): Promise<void> {
    const selection = this.selection;
    const service = dataPlaneService(this.rootStore, "data-mapper-data-source");
    if (!selection?.hasProfile || !service) return;
    const ended = sessionMark(this.rootStore);
    const answer = (await service.create({ action: "status", ...selection.dataPlaneQuery })) as {
      connected?: boolean;
      name?: string | null;
    } | null;
    // Past a sign-out: no status latched over the reset store.
    if (ended()) return;
    runInAction(() => {
      this.sourceConnected = answer?.connected === true;
      this.sourceName = this.sourceConnected ? (answer?.name ?? null) : null;
      this.statusLoaded = true;
    });
  }

  // ---- Choices ---------------------------------------------------------------

  /** Choose the mapping configuration to run. Another config's table map means nothing here. */
  selectConfig(id: string | null) {
    if (this.running || this.starting) return;
    const next = id || null;
    if (!sameId(next, this.mappingConfigId)) {
      this.targetTables = {};
      this.sourceFilters = {};
    }
    this.mappingConfigId = next;
    this.configDetailError = null;
  }

  setTargetTable(sourceTable: string, targetTable: string) {
    if (this.running || this.starting) return;
    const next = { ...this.targetTables };
    if (targetTable.trim()) next[sourceTable] = targetTable.trim();
    else delete next[sourceTable];
    this.targetTables = next;
  }

  setWriteMode(mode: DataMapperWriteMode) {
    if (this.running || this.starting || this.confirmOpen) return;
    this.writeMode = mode;
  }

  setIncludeMetadata(on: boolean) {
    if (this.running || this.starting || this.confirmOpen) return;
    this.includeMetadata = on;
  }

  /** Add a filter row to a source table; answers its key (-1 when the form is locked). */
  addSourceFilter(table: string): number {
    if (this.running || this.starting || this.confirmOpen) return -1;
    if ((this.sourceFilters[table]?.length ?? 0) >= MAX_SOURCE_FILTERS) return -1;
    const key = ++this.filterKey;
    const column = this.filterColumns[table]?.[0] ?? "";
    this.sourceFilters = {
      ...this.sourceFilters,
      [table]: [...(this.sourceFilters[table] ?? []), { key, column, operator: "eq", value: "", enabled: true }],
    };
    void this.loadFilterColumns(table);
    return key;
  }

  updateSourceFilter(table: string, key: number, patch: Partial<Omit<BrowseFilter, "key">>) {
    if (this.running || this.starting || this.confirmOpen) return;
    const rows = this.sourceFilters[table];
    if (!rows) return;
    this.sourceFilters = { ...this.sourceFilters, [table]: rows.map((f) => (f.key === key ? { ...f, ...patch } : f)) };
  }

  removeSourceFilter(table: string, key: number) {
    if (this.running || this.starting || this.confirmOpen) return;
    const rows = (this.sourceFilters[table] ?? []).filter((f) => f.key !== key);
    const next = { ...this.sourceFilters };
    if (rows.length > 0) next[table] = rows;
    else delete next[table];
    this.sourceFilters = next;
  }

  /** A source table's column names, for the filter editor. Best effort: a refusal leaves the column typed by hand. */
  async loadFilterColumns(table: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-source-tables");
    const selection = this.selection;
    if (!service || !selection?.hasProfile || !this.sourceConnected || this.filterColumns[table]) return;
    try {
      const details = (await service.get(table, { query: { ...selection.dataPlaneQuery } })) as {
        columns?: Array<{ name?: unknown }>;
      } | null;
      const names = (details?.columns ?? []).map((c) => c.name).filter((n): n is string => typeof n === "string");
      runInAction(() => {
        this.filterColumns = { ...this.filterColumns, [table]: names };
      });
    } catch {
      // The column stays free text.
    }
  }

  // ---- Starting --------------------------------------------------------------

  /** Run a dry run: nothing is written. */
  async dryRun(): Promise<boolean> {
    return this.start("dry-run", "insert");
  }

  /** Ask for a write: opens the confirmation. Nothing is sent until `confirmWrite`. */
  requestWrite() {
    if (!this.canWrite) return;
    this.confirmWriteMode = this.writeMode;
    this.confirmOpen = true;
  }

  cancelConfirm() {
    this.confirmOpen = false;
  }

  /** The confirmation's "Write" button: the only way a write import starts. */
  async confirmWrite(): Promise<boolean> {
    if (!this.confirmOpen) return false;
    this.confirmOpen = false;
    if (!this.canWrite) return false;
    return this.start("write", this.confirmWriteMode);
  }

  private async start(mode: DataMapperImportMode, writeMode: DataMapperWriteMode): Promise<boolean> {
    const service = this.importsService;
    const selection = this.selection;
    const config = this.selectedConfig;
    if (!service || !selection?.hasProfile || !config || !this.canStart) return false;
    const targetTables = Object.fromEntries(
      Object.entries(this.targetTables).filter(([t, v]) => this.sourceTables.includes(t) && v.trim() !== "")
    );
    const sourceFilters = this.sourceFiltersBody;
    const body: Record<string, unknown> = {
      ...selection.dataPlaneQuery,
      mappingConfigId: config.id,
      mode,
      ...(mode === "write" ? { writeMode } : {}),
      ...(Object.keys(targetTables).length > 0 ? { targetTables } : {}),
      ...(Object.keys(sourceFilters).length > 0 ? { sourceFilters } : {}),
      ...(this.includeMetadata ? { includeMetadata: true } : {}),
    };
    this.stopPolling();
    this.starting = mode;
    this.startError = null;
    this.importId = null;
    this.mode = mode;
    this.importWriteMode = mode === "write" ? writeMode : null;
    this.status = null;
    this.phase = null;
    this.done = 0;
    this.total = 0;
    this.result = null;
    this.summary = null;
    this.importError = null;
    this.cancelState = "idle";
    this.cancelMessage = null;
    this.lastHeardAt = Date.now();
    this.subscribe();
    const ended = sessionMark(this.rootStore);
    try {
      const answer = (await service.create(body)) as { importId?: string } | null;
      // Started on the server by the person who signed out: this tab must not
      // adopt it (its id, its fallback timer).
      if (ended()) return false;
      const importId = answer?.importId;
      if (!importId) throw new Error("The API did not say which import it started.");
      runInAction(() => {
        this.starting = null;
        this.importId = importId;
        if (this.status === null) {
          this.status = "running";
          this.phase = "preparing";
        }
      });
      // A dry run can finish before its id was known here, so its events were dropped: read it once now.
      await this.readImport();
      if (ended()) return false;
      this.scheduleFallback();
      return true;
    } catch (err) {
      if (ended()) return false;
      runInAction(() => {
        this.starting = null;
        this.mode = null;
        this.importWriteMode = null;
        this.startError = importErrorMessage(err, "The import could not be started.");
      });
      return false;
    }
  }

  // ---- Progress and the result ------------------------------------------------

  private subscribe() {
    if (this.unsubscribe || !this.tabId) return;
    this.unsubscribe = subscribeTabEvents(this.rootStore, {
      path: DATA_MAPPER_IMPORTS_PATH,
      events: IMPORT_EVENT_NAMES,
      tabId: this.tabId,
      onEvent: (event, payload) => this.onImportEvent(event, payload as Partial<DataMapperImportEventPayload>),
    });
  }

  /** One of THIS tab's import events (tabEvents dropped the others); still only for the import started here. */
  onImportEvent(event: (typeof IMPORT_EVENT_NAMES)[number], payload: Partial<DataMapperImportEventPayload>) {
    if (!this.importId || typeof payload.importId !== "string" || !sameId(payload.importId, this.importId)) return;
    this.lastHeardAt = Date.now();
    if (event === DATA_MAPPER_IMPORT_EVENTS.progress) this.onProgress(payload);
    else this.applyView({ ...payload, importId: this.importId });
  }

  /** `import-progress`: where the running import is. */
  private onProgress(event: Partial<DataMapperImportEventPayload>) {
    if (isFinished(this.status)) return;
    if (event.phase) this.phase = event.phase;
    this.done = Number(event.done) || 0;
    this.total = Number(event.total) || 0;
  }

  /** A final event or a read: the import's status, and once finished its result. */
  applyView(view: Partial<DataMapperImportView> & { importId: string; summary?: DataMapperImportSummary }) {
    if (!this.importId || view.importId !== this.importId) return;
    if (isFinished(this.status)) return;
    if (view.status) this.status = view.status;
    if (view.phase) this.phase = view.phase;
    if (typeof view.done === "number") this.done = view.done;
    if (typeof view.total === "number") this.total = view.total;
    if (view.result) this.result = view.result;
    if (view.summary) this.summary = view.summary;
    if (view.error) this.importError = importFailureSentence(view.error);
    if (isFinished(this.status)) {
      this.stopPolling();
      // A "too late, committing" notice is answered by the result itself: drop it (Codex r1).
      if (this.cancelState === "too-late") this.cancelMessage = null;
      if (this.cancelState === "requesting" || this.cancelState === "too-late") this.cancelState = "idle";
      // An event that said "finished" without the report: read it.
      if (!this.result && view.result === undefined) void this.readImport();
    }
  }

  /** Read the import (the fallback for missed events, and the result when an event lacks it). */
  async readImport(): Promise<void> {
    const service = this.importsService;
    const id = this.importId;
    if (!service || !id) return;
    const ended = sessionMark(this.rootStore);
    try {
      const view = (await service.get(id)) as DataMapperImportView;
      if (ended()) return;
      runInAction(() => {
        if (id !== this.importId) return;
        // A read after the final event still fills in a result the event lacked.
        if (isFinished(this.status) && !this.result && view.result) this.result = view.result;
        else this.applyView({ ...view, importId: id });
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        if (id !== this.importId) return;
        if (refusalReason(err) === "import-not-found") {
          this.stopPolling();
          if (!isFinished(this.status)) this.status = "failed";
          this.importError = "HQ no longer has this import (the API restarted, or it finished over an hour ago).";
        }
        // Anything else (a blip): the next read or event tries again.
      });
    }
  }

  /**
   * The fallback read: due once the import has been silent for
   * IMPORT_EVENT_SILENCE_MS, then every IMPORT_POLL_MS while the silence lasts.
   * An event in between pushes it back (the timer re-checks when it fires).
   */
  private scheduleFallback() {
    this.stopPolling();
    if (!this.importId || isFinished(this.status)) return;
    const silentFor = Date.now() - this.lastHeardAt;
    const delay = silentFor >= IMPORT_EVENT_SILENCE_MS ? IMPORT_POLL_MS : IMPORT_EVENT_SILENCE_MS - silentFor;
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      if (Date.now() - this.lastHeardAt < IMPORT_EVENT_SILENCE_MS) {
        this.scheduleFallback();
        return;
      }
      void this.readImport().then(() => this.scheduleFallback());
    }, delay);
  }

  private stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  // ---- Cancel ----------------------------------------------------------------

  /**
   * Cancel the running import (rolled back). Past the last point it can be
   * rolled back the API answers 409 'import-committing': "too late, committing".
   */
  async cancel(): Promise<void> {
    const service = this.importsService;
    const id = this.importId;
    if (!service?.patch || !id || !this.running || this.cancelState === "requesting") return;
    this.cancelState = "requesting";
    this.cancelMessage = null;
    try {
      const view = (await service.patch(id, { action: "cancel" })) as DataMapperImportView | null;
      runInAction(() => {
        if (id !== this.importId) return;
        this.cancelState = "idle";
        if (view) this.applyView({ ...view, importId: id });
        // The import finished on its own before the cancel reached it: say so, not "cancelled".
        if (view && view.status !== "cancelled" && view.status !== "running") {
          this.cancelMessage = "The import finished before the cancel reached it.";
        }
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (id !== this.importId) return;
        if (refusalReason(err) === "import-committing") {
          this.cancelState = "too-late";
          this.cancelMessage = TOO_LATE_TO_CANCEL_MESSAGE;
        } else {
          this.cancelState = "failed";
          this.cancelMessage = importErrorMessage(err, "The import could not be cancelled.");
        }
      });
    }
  }

  /** Put the finished import away, to run another. */
  clearImport() {
    if (this.running || this.starting) return;
    this.importId = null;
    this.mode = null;
    this.importWriteMode = null;
    this.status = null;
    this.phase = null;
    this.done = 0;
    this.total = 0;
    this.result = null;
    this.summary = null;
    this.importError = null;
    this.cancelState = "idle";
    this.cancelMessage = null;
  }

  clearStartError() {
    this.startError = null;
  }

  goToTarget() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_TARGET_ROUTE);
  }

  goToSource() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_SOURCE_ROUTE);
  }

  /**
   * Route exit: stop listening and reading. A running import keeps running on
   * the server; its id stays, so coming back catches up with it.
   */
  reset() {
    this.loadSeq++;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.stopPolling();
    this.confirmOpen = false;
    this.startError = null;
    this.configsError = null;
    this.configsLoading = false;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.mappingConfigId = null;
    this.targetTables = {};
    this.writeMode = "insert";
    this.sourceFilters = {};
    this.includeMetadata = false;
    this.filterColumns = {};
    this.confirmWriteMode = "insert";
    this.sourceConnected = false;
    this.sourceName = null;
    this.statusLoaded = false;
    this.configDetailError = null;
    this.starting = null;
    this.importId = null;
    this.mode = null;
    this.importWriteMode = null;
    this.status = null;
    this.phase = null;
    this.done = 0;
    this.total = 0;
    this.result = null;
    this.summary = null;
    this.importError = null;
    this.cancelState = "idle";
    this.cancelMessage = null;
    this.lastHeardAt = 0;
  }
}

export default DataMapperImportStore;
